import { BarChart2, Settings, CalendarCheck, CheckSquare, ListTodo, Star, Award, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import NavBar from "@/components/NavBar";
import FeatureCard from "@/components/FeatureCard";
import { LiveClock } from "@/components/LiveClock";
import { QuickAddTask } from "@/components/QuickAddTask";
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import TaskInputForm from "@/components/TaskInputForm";
import TaskList from "@/components/TaskList";
import WeeklyGoalsDialog from "@/components/WeeklyGoalsDialog";
import CommonTasksDialog from "@/components/CommonTasksDialog";
import { Calendar } from "@/components/Calendar";
import TaskScore from "@/components/TaskScore";
import { useTask } from "@/contexts/TaskContext";
import { format } from "date-fns";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, Clock as ClockIcon, Calendar as CalendarIcon } from "lucide-react";
import { PomodoroTimer } from "@/components/PomodoroTimer";

export default function ClockThatHome() {
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [isWeeklyGoalsDialogOpen, setIsWeeklyGoalsDialogOpen] = useState(false);
  const [isCommonTasksDialogOpen, setIsCommonTasksDialogOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isTaskScoreOpen, setIsTaskScoreOpen] = useState(false);
  const [isCompletedTasksOpen, setIsCompletedTasksOpen] = useState(false);
  const [isPomodoroOpen, setIsPomodoroOpen] = useState(false);
  const [showRemindersView, setShowRemindersView] = useState(false);
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  
  const { completedTasks } = useTask();

  const completedTasksForDisplay = useMemo(() => {
    return completedTasks.map(task => ({
      title: task.title,
      completedAt: task.completedAt || task.createdAt
    }));
  }, [completedTasks]);

  const tasksByDate = useMemo(() => {
    if (!completedTasksForDisplay.length) return {};
    return completedTasksForDisplay.reduce((acc: Record<string, {
      title: string;
      completedAt: string;
    }[]>, task) => {
      const date = format(new Date(task.completedAt), 'yyyy-MM-dd');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(task);
      return acc;
    }, {});
  }, [completedTasksForDisplay]);

  const sortedDates = useMemo(() => {
    return Object.keys(tasksByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  }, [tasksByDate]);

  const toggleCalendarView = () => {
    setShowRemindersView(prev => !prev);
  };

  return <div className="min-h-screen bg-gradient-to-b from-[#54a0e7] to-[#2277cc] dark:from-[#3a78b8] dark:to-[#1a5eb3] text-white">
      <NavBar />

      <section className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <LiveClock />
        
        <h1 className="text-5xl font-bold mt-10 mb-2">CLOCK-THAT</h1>
        <h2 className="font-bold mb-4 text-3xl">Optimize Your Time with Clock-That: Your Ultimate Time Management Tool.&quot;</h2>
        <p className="text-xl max-w-2xl mb-10 text-left">
          Forget overwhelming to-do lists and endless weekly plans.
          Clock-That helps you win the day — one focused time block at a time.
          
          -Log in / -Add task
          -Assign time blocks.
          -Get it done — one day at a time.

          No fluff. Just focus.
          That's The Clock-That way.
        </p>
        
        <Button className="bg-[#39a1ff] hover:bg-[#2b7fce] text-white text-xl font-semibold px-12 py-6 rounded-full shadow-lg shadow-[#0000001a]">Get Started for free
      </Button>
      </section>

      <section className="py-16 px-4 bg-white dark:bg-gray-900 transition-colors duration-300">
        <h2 className="text-4xl font-bold text-[#0f4c8a] dark:text-[#54a0e7] text-center mb-12">Get Started</h2>
        
        {/* Quick Add Task - Prominent placement */}
        <div className="max-w-2xl mx-auto mb-16">
          <QuickAddTask />
        </div>

        <h3 className="text-2xl font-semibold text-[#0f4c8a] dark:text-[#54a0e7] text-center mb-8">Core Features</h3>
        
        {/* Core Features - Always visible */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div onClick={() => setIsTaskDialogOpen(true)} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
            <FeatureCard icon={<ClockIcon className="text-[#1a5eb3]" size={64} />} title="Today's Task" description="View and manage your daily tasks" />
          </div>
          
          <div onClick={() => setIsCompletedTasksOpen(true)} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
            <FeatureCard icon={<CheckSquare className="text-[#1a5eb3]" size={64} />} title="Completed Tasks" description="Track your accomplishments" completedTasks={completedTasksForDisplay} />
          </div>
          
          <div onClick={() => setIsPomodoroOpen(true)} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
            <FeatureCard 
              icon={<Clock className="text-[#1a5eb3]" size={64} />} 
              title="Focus Timer" 
              description="Pomodoro technique timer" 
            />
          </div>
        </div>

        {/* More Features - Progressive Disclosure */}
        <div className="max-w-6xl mx-auto">
          <Button
            onClick={() => setShowMoreFeatures(!showMoreFeatures)}
            variant="outline"
            className="mx-auto mb-8 flex items-center gap-2 text-[#0f4c8a] dark:text-[#54a0e7] border-[#0f4c8a] dark:border-[#54a0e7] hover:bg-[#0f4c8a]/5 dark:hover:bg-[#54a0e7]/5"
          >
            {showMoreFeatures ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Show Less Features
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Show More Features
              </>
            )}
          </Button>

          {showMoreFeatures && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-in slide-in-from-top-5 duration-300">
              <div onClick={() => setIsWeeklyGoalsDialogOpen(true)} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
                <FeatureCard icon={<BarChart2 className="text-[#1a5eb3]" size={64} />} title="Weekly Goals" description="Set and track weekly objectives" />
              </div>
              
              <div onClick={() => setIsCommonTasksDialogOpen(true)} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
                <FeatureCard icon={<Star className="text-[#1a5eb3]" size={64} />} title="Common Tasks" description="Quick access to frequent tasks" />
              </div>

              <div onClick={() => setIsCalendarOpen(true)} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
                <FeatureCard icon={<CalendarCheck className="text-[#1a5eb3]" size={64} />} title="Calendar" description="Schedule and view reminders" />
              </div>
            </div>
          )}
        </div>
      </section>

      <Dialog open={isPomodoroOpen} onOpenChange={setIsPomodoroOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Focus Timer</DialogTitle>
            <DialogDescription>
              Use the Pomodoro technique to enhance your productivity: 25 minutes focused work, followed by 5 minute breaks.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <PomodoroTimer />
          </div>
          <DialogClose asChild>
            <Button className="mt-4" onClick={() => setIsPomodoroOpen(false)}>Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>

      <Dialog open={isTaskDialogOpen} onOpenChange={setIsTaskDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Today's Tasks</DialogTitle>
            <DialogDescription>
              Enter your tasks for today along with their scheduled start times.
            </DialogDescription>
          </DialogHeader>
          <TaskInputForm onClose={() => setIsTaskDialogOpen(false)} />
          <DialogClose asChild>
            <Button className="mt-4" onClick={() => setIsTaskDialogOpen(false)}>Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>

      <WeeklyGoalsDialog open={isWeeklyGoalsDialogOpen} onOpenChange={setIsWeeklyGoalsDialogOpen} />
      
      <CommonTasksDialog open={isCommonTasksDialogOpen} onOpenChange={setIsCommonTasksDialogOpen} />
      
      <Dialog open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Calendar</DialogTitle>
            <DialogDescription>
              Schedule events and set reminders for future tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Calendar onClose={() => setIsCalendarOpen(false)} showRemindersView={showRemindersView} onToggleView={toggleCalendarView} />
          </div>
          <DialogClose asChild>
            <Button className="mt-4" onClick={() => setIsCalendarOpen(false)}>Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isTaskScoreOpen} onOpenChange={setIsTaskScoreOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Monthly Task Score</DialogTitle>
            <DialogDescription>
              Track your task completion rate for this month.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <TaskScore />
          </div>
          <DialogClose asChild>
            <Button className="mt-4" onClick={() => setIsTaskScoreOpen(false)}>Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isCompletedTasksOpen} onOpenChange={setIsCompletedTasksOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Completed Tasks</DialogTitle>
            <DialogDescription>
              View all your completed tasks organized by date.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
           {completedTasksForDisplay.length > 0 ? <Accordion type="single" collapsible className="w-full">
                {sortedDates.map(dateStr => <AccordionItem key={dateStr} value={dateStr}>
                    <AccordionTrigger className="text-sm px-2 py-1">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        {format(new Date(dateStr), 'EEE, MMM d, yyyy')}
                        <span className="ml-2 text-xs text-gray-500">
                          ({tasksByDate[dateStr].length})
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 pl-2">
                        {tasksByDate[dateStr].map((task, idx) => <div key={idx} className="flex items-center justify-between text-left p-2 bg-green-50 dark:bg-green-900/30 rounded-md">
                            <span className="flex items-center text-sm text-green-700 dark:text-green-300">
                              <Check className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                              {task.title}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              <ClockIcon className="h-3 w-3 inline mr-1" />
                              {format(new Date(task.completedAt), 'h:mm a')}
                            </span>
                          </div>)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion> : <p className="text-center text-gray-500 dark:text-gray-400 py-4">No completed tasks yet</p>}
          </div>
          <DialogClose asChild>
            <Button className="mt-4" onClick={() => setIsCompletedTasksOpen(false)}>Close</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </div>;
}
